/**
 * sc-3031 — ResetPaymentPlanModal
 *
 * Confirm-Dialog mit Vorschau für das Zurücksetzen des Zahlungsplans einer
 * abgerechneten Lesson-Booking auf den Kurs-Standard.
 *
 * Beim Öffnen wird die Mutation mit `previewOnly: true` aufgerufen — das
 * Ergebnis liefert entweder einen Block-Reason (Hinweismeldung statt
 * Vorschau) oder die Vorher/Nachher-Daten. Erst der Confirm-Klick ruft
 * die Mutation mit `previewOnly: false` und führt den echten Reset aus.
 *
 * Bezahlte Posten erscheinen in der "Nachher"-Spalte unverändert mit dem
 * "Bezahlt"-Tag. Neue Posten werden chronologisch eingeordnet.
 */
import React, {FC, useEffect, useMemo, useState} from "react";
import {Modal, Button, Alert, Tag, Spin, message, Divider} from "antd";
import moment from "moment";
import {
    useResetPaymentPlanOfInvoicedLessonBookingMutation,
    ResetPaymentPlanOfInvoicedLessonBookingMutation,
    ResetPaymentPlanBlockedReason,
} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {renderAmount} from "../../../helpers/renderAmount";
import {erasedRecordMessage} from "../../../helpers/erasedRecordMessage";

type ResetResult = ResetPaymentPlanOfInvoicedLessonBookingMutation["resetPaymentPlanOfInvoicedLessonBooking"];

type RateSnapshot = ResetResult["oldScheduledPayments"][number];

type ResetPaymentPlanModalProps = {
    open: boolean;
    bookingId: string;
    onClose: () => void;
    onSuccess?: () => void;
};

const formatDate = (raw: string | Date | null | undefined): string => {
    if (!raw) return "—";
    const m = moment(raw);
    return m.isValid() ? m.format("DD.MM.YYYY") : String(raw);
};

const RatesList: FC<{rates: RateSnapshot[]; mode: "old" | "new"; paidLabel: string}> = ({rates, mode, paidLabel}) => {
    const sorted = useMemo(() => {
        // Tombstoned Posten ausfiltern in der "Nachher"-Spalte
        const filtered = mode === "new" ? rates.filter((r) => !r.deletedAt) : rates;
        return [...filtered].sort((a, b) => {
            const aT = a.paymentDue ? new Date(a.paymentDue).getTime() : Number.POSITIVE_INFINITY;
            const bT = b.paymentDue ? new Date(b.paymentDue).getTime() : Number.POSITIVE_INFINITY;
            return aT - bT;
        });
    }, [rates, mode]);

    if (sorted.length === 0) {
        return <div style={{color: "#999", fontStyle: "italic"}}>—</div>;
    }

    return (
        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
            {sorted.map((r, idx) => {
                const isDeleted = Boolean(r.deletedAt);
                const isManual = Boolean(r.manuallyAdjustedAt);
                return (
                    <div
                        key={r.id ?? idx}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "4px 8px",
                            borderLeft: r.paid ? "3px solid #52c41a" : isDeleted ? "3px solid #ff4d4f" : "3px solid #d9d9d9",
                            background: isDeleted ? "#fff1f0" : r.paid ? "#f6ffed" : "transparent",
                            opacity: isDeleted ? 0.7 : 1,
                            textDecoration: isDeleted ? "line-through" : "none",
                        }}
                    >
                        <span style={{minWidth: 90, color: "#666"}}>{formatDate(r.paymentDue)}</span>
                        <span style={{minWidth: 80, fontWeight: 500}}>{renderAmount({amount: Number(r.paymentAmount ?? 0)})}</span>
                        {r.paid && <Tag color="green">{paidLabel}</Tag>}
                        {isManual && !isDeleted && <Tag color="orange">manuell</Tag>}
                        {isDeleted && <Tag color="red">gelöscht</Tag>}
                    </div>
                );
            })}
        </div>
    );
};

const ResetPaymentPlanModal: FC<ResetPaymentPlanModalProps> = ({open, bookingId, onClose, onSuccess}) => {
    const [previewData, setPreviewData] = useState<ResetResult | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const [resetMutation] = useResetPaymentPlanOfInvoicedLessonBookingMutation();

    // Bei Open: Preview laden
    useEffect(() => {
        if (!open || !bookingId) {
            return;
        }
        let canceled = false;
        setPreviewData(null);
        setPreviewLoading(true);
        resetMutation({variables: {bookingId, previewOnly: true}})
            .then((res) => {
                if (canceled) return;
                if (res.data?.resetPaymentPlanOfInvoicedLessonBooking) {
                    setPreviewData(res.data.resetPaymentPlanOfInvoicedLessonBooking);
                }
            })
            .catch((err) => {
                if (canceled) return;
                message.error(erasedRecordMessage(err) ?? i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.errorTitle());
                // eslint-disable-next-line no-console
                console.error("ResetPaymentPlan preview failed", err);
            })
            .finally(() => {
                if (canceled) return;
                setPreviewLoading(false);
            });
        return () => {
            canceled = true;
        };
    }, [open, bookingId, resetMutation]);

    const handleConfirm = async () => {
        setResetLoading(true);
        try {
            const res = await resetMutation({
                variables: {bookingId, previewOnly: false},
                refetchQueries: [
                    "GetBookingByIdWithCourseLessonBookings",
                    "BookingHistoryByIdWithBookingInfo",
                    "BookingHistoryByBookingId",
                    "getUnpaidScheduledPayments",
                    "getPaidScheduledPayments",
                ],
            });
            const data = res.data?.resetPaymentPlanOfInvoicedLessonBooking;
            if (data?.blockedReason) {
                setPreviewData(data);
                setResetLoading(false);
                return;
            }
            message.success(i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.success());
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("ResetPaymentPlan reset failed", err);
            Modal.error({
                title: i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.errorTitle(),
                content: erasedRecordMessage(err) ?? (err instanceof Error ? err.message : String(err)),
            });
        } finally {
            setResetLoading(false);
        }
    };

    // i18n: Volle Pfade pro Aufruf — der Proxy resettet `path` nach jedem
    // apply, daher dürfen KEINE Zwischenreferenzen wie `const t = i18n.x.y.z`
    // verwendet werden (nur erster Aufruf würde funktionieren).
    const blockedMessageFor = (reason: ResetPaymentPlanBlockedReason | null | undefined): string => {
        switch (reason) {
            case ResetPaymentPlanBlockedReason.ActiveMollie:
                return i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.blocked.ACTIVE_MOLLIE();
            case ResetPaymentPlanBlockedReason.PaidExceedsStandard:
                return i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.blocked.PAID_EXCEEDS_STANDARD();
            case ResetPaymentPlanBlockedReason.NoBookedLessons:
                return i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.blocked.NO_BOOKED_LESSONS();
            case ResetPaymentPlanBlockedReason.UnsupportedPaymentType:
                return i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.blocked.UNSUPPORTED_PAYMENT_TYPE();
            case ResetPaymentPlanBlockedReason.PartiallyPaid:
                return i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.blocked.PARTIALLY_PAID();
            default:
                return "";
        }
    };

    const isBlocked = Boolean(previewData?.blockedReason);
    // paidLabel ist OK als String-Variable: das Ergebnis von `()` ist
    // bereits ein aufgelöster String, kein Proxy mehr.
    const paidLabel = i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.paidTag();

    return (
        <Modal
            visible={open}
            title={i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.title()}
            onCancel={onClose}
            width={780}
            footer={[
                <Button key="cancel" onClick={onClose} disabled={resetLoading}>
                    {i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.cancel()}
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    danger
                    onClick={handleConfirm}
                    loading={resetLoading}
                    disabled={previewLoading || isBlocked || !previewData}
                >
                    {i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.confirm()}
                </Button>,
            ]}
        >
            <p>{i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.intro()}</p>
            <div style={{background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 4, padding: "12px 16px", marginBottom: 16}}>
                <div style={{fontWeight: 500, marginBottom: 6}}>
                    {i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.explainHeader()}
                </div>
                <ul style={{margin: 0, paddingLeft: 20}}>
                    <li>{i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.explainBullet1()}</li>
                    <li>{i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.explainBullet2()}</li>
                    <li>{i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.explainBullet3()}</li>
                    <li>{i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.explainBullet4()}</li>
                </ul>
                <div style={{marginTop: 8, fontWeight: 500, color: "#52c41a"}}>
                    {i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.explainPaidNote()}
                </div>
            </div>

            {previewLoading && (
                <div style={{display: "flex", justifyContent: "center", padding: 24}}>
                    <Spin tip={i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.loadingPreview()} />
                </div>
            )}

            {!previewLoading && isBlocked && previewData && (
                <Alert
                    type="warning"
                    showIcon
                    message={i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.blockedTitle()}
                    description={previewData.blockedMessage ?? blockedMessageFor(previewData.blockedReason)}
                />
            )}

            {!previewLoading && !isBlocked && previewData && (
                <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24}}>
                    <div>
                        <h4>{i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.sectionOld()}</h4>
                        <RatesList rates={previewData.oldScheduledPayments} mode="old" paidLabel={paidLabel} />
                        <Divider style={{margin: "12px 0"}} />
                        <div style={{fontWeight: 500}}>
                            {i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.totalLabel()}: {renderAmount({amount: Number(previewData.oldTotalGrossPrice ?? 0)})}
                        </div>
                        {previewData.oldDiscount !== null && previewData.oldDiscount !== undefined && previewData.oldDiscount !== 0 && (
                            <div style={{color: "#fa8c16"}}>
                                {i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.removedDiscount()}: {renderAmount({amount: Number(previewData.oldDiscount)})}
                            </div>
                        )}
                        {previewData.oldCreditedBalance !== null && previewData.oldCreditedBalance !== undefined && previewData.oldCreditedBalance !== 0 && (
                            <div style={{color: "#fa8c16"}}>
                                {i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.removedCredit()}: {renderAmount({amount: Number(previewData.oldCreditedBalance)})}
                            </div>
                        )}
                    </div>
                    <div>
                        <h4>{i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.sectionNew()}</h4>
                        <RatesList rates={previewData.newScheduledPayments} mode="new" paidLabel={paidLabel} />
                        <Divider style={{margin: "12px 0"}} />
                        <div style={{fontWeight: 500}}>
                            {i18n.containers.invoice.SettlementDetails.resetPaymentPlanModal.totalLabel()}: {renderAmount({amount: Number(previewData.newTotalGrossPrice ?? 0)})}
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ResetPaymentPlanModal;
